/**
 * Admin — Structure extras: Years, Subjects, Programs (college T-10), Rooms.
 *
 * Years reuses the calendar summary + creation endpoints. Programs and Rooms
 * are new (migration 033_structure). Subjects reuses /subjects with a
 * dedicated rename/delete UI. Rollforward and program-scoped year handling
 * ships with the college-mode iteration.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import {
  Alert, Button, Card, Empty, Form, Input, InputNumber, Modal, Popconfirm, Select, Skeleton, Space, Switch,
  Table, Tag, Typography, message,
} from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import type {
  CreateProgramRequest, CreateRoomRequest, CreateSubjectRequest, CreateTermRequest, CreateHolidayRequest,
  CreateSessionRequest, ProgramRow, RoomKind, RoomRow, SubjectRow,
  UpdateProgramRequest, UpdateRoomRequest,
} from '@velora/contracts';
import { ApiError, productApi } from '../../../api';

function extractErrMsg(err: unknown): string | null {
  if (!err) return null;
  if (err instanceof ApiError) return (err.body as { message?: string } | null)?.message ?? err.message;
  return err instanceof Error ? err.message : String(err);
}

/* =================== Years =================== */

export function YearsPage() {
  const { institution = '' } = useParams<{ institution: string }>();
  const qc = useQueryClient();
  const [addSession, setAddSession] = useState(false);
  const [addTerm, setAddTerm] = useState(false);
  const [addHoliday, setAddHoliday] = useState(false);
  const cal = useQuery({
    queryKey: ['calendar', institution],
    queryFn: () => productApi.getCalendar(institution),
    enabled: !!institution,
  });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['calendar', institution] });

  if (cal.isLoading || !cal.data) return <Skeleton active paragraph={{ rows: 6 }} />;
  const current = cal.data.current_session;

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
        <div style={{ flex: 1 }}>
          <Typography.Title level={3} style={{ margin: 0 }}>Academic years</Typography.Title>
          <Typography.Text type="secondary" style={{ fontSize: 12.5 }}>
            Sessions, terms and holidays. Rollforward (promotion + fee re-issue) is at <a href={`/i/${institution}/admin/promotions`}>Promotions</a>.
          </Typography.Text>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddSession(true)}>New session</Button>
      </div>

      <Card size="small" title={`Sessions (${cal.data.sessions.length})`}>
        {cal.data.sessions.length === 0
          ? <Empty description="No sessions defined." style={{ padding: 20 }} />
          : (
            <Table
              size="small" pagination={false} rowKey="id"
              dataSource={cal.data.sessions}
              columns={[
                { title: 'Label', dataIndex: 'label', key: 'l',
                  render: (v: string, r) => <><b>{v}</b> {r.is_current && <Tag color="success">current</Tag>}</> },
                { title: 'Start', dataIndex: 'starts_on', key: 's' },
                { title: 'End', dataIndex: 'ends_on', key: 'e' },
              ]}
            />
          )}
      </Card>

      {current && (
        <div style={{ display: 'flex', gap: 8 }}>
          <Button icon={<PlusOutlined />} onClick={() => setAddTerm(true)}>Add term</Button>
          <Button icon={<PlusOutlined />} onClick={() => setAddHoliday(true)}>Add holiday</Button>
          <Typography.Text type="secondary" style={{ alignSelf: 'center', fontSize: 12 }}>
            for <b>{current.label}</b>
          </Typography.Text>
        </div>
      )}

      <Card size="small" title={`Terms (${cal.data.terms.length})`}>
        {cal.data.terms.length === 0
          ? <Empty description="No terms defined for the current session." style={{ padding: 20 }} />
          : (
            <Table size="small" pagination={false} rowKey="id"
              dataSource={cal.data.terms}
              columns={[
                { title: 'Label', dataIndex: 'label', key: 'l' },
                { title: 'Start', dataIndex: 'starts_on', key: 's' },
                { title: 'End', dataIndex: 'ends_on', key: 'e' },
              ]}
            />
          )}
      </Card>

      <Card size="small" title={`Holidays (${cal.data.holidays.length})`}>
        {cal.data.holidays.length === 0
          ? <Empty description="No holidays yet." style={{ padding: 20 }} />
          : (
            <Table size="small" pagination={{ pageSize: 12 }} rowKey="id"
              dataSource={cal.data.holidays}
              columns={[
                { title: 'Date', dataIndex: 'date', key: 'd', width: 120,
                  render: (v: string) => <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 12 }}>{v}</span> },
                { title: 'Label', dataIndex: 'label', key: 'l' },
              ]}
            />
          )}
      </Card>

      <SessionModal open={addSession} institution={institution} onClose={() => setAddSession(false)} onDone={invalidate} />
      {current && <>
        <TermModal open={addTerm} institution={institution} sessionId={current.id}
          onClose={() => setAddTerm(false)} onDone={invalidate} />
        <HolidayModal open={addHoliday} institution={institution} sessionId={current.id}
          onClose={() => setAddHoliday(false)} onDone={invalidate} />
      </>}
    </Space>
  );
}

function SessionModal({
  open, institution, onClose, onDone,
}: { open: boolean; institution: string; onClose: () => void; onDone: () => void }) {
  const [form] = Form.useForm<CreateSessionRequest>();
  const m = useMutation({
    mutationFn: (input: CreateSessionRequest) => productApi.createSession(institution, input),
    onSuccess: () => { form.resetFields(); onDone(); onClose(); void message.success('Session added'); },
  });
  const err = extractErrMsg(m.error);
  return (
    <Modal open={open} title="New session" onCancel={() => { m.reset(); onClose(); }}
      okText="Add" confirmLoading={m.isPending} onOk={() => form.submit()} destroyOnClose>
      <Form form={form} layout="vertical"
        onFinish={(v) => m.mutate({
          label: v.label.trim(),
          starts_on: v.starts_on,
          ends_on: v.ends_on,
          is_current: v.is_current ?? false,
        })}
      >
        <Form.Item name="label" label="Label" rules={[{ required: true }]}><Input placeholder="e.g. 2026-27" /></Form.Item>
        <Space size={12}>
          <Form.Item name="starts_on" label="Starts" rules={[{ required: true }]}>
            <Input placeholder="yyyy-mm-dd" style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }} />
          </Form.Item>
          <Form.Item name="ends_on" label="Ends" rules={[{ required: true }]}>
            <Input placeholder="yyyy-mm-dd" style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }} />
          </Form.Item>
        </Space>
        <Form.Item name="is_current" label="Set as current session" valuePropName="checked"><Switch /></Form.Item>
        {err && <Alert type="error" showIcon message={err} />}
      </Form>
    </Modal>
  );
}

function TermModal({
  open, institution, sessionId, onClose, onDone,
}: { open: boolean; institution: string; sessionId: string; onClose: () => void; onDone: () => void }) {
  const [form] = Form.useForm<CreateTermRequest>();
  const m = useMutation({
    mutationFn: (input: CreateTermRequest) => productApi.createTerm(institution, input),
    onSuccess: () => { form.resetFields(); onDone(); onClose(); void message.success('Term added'); },
  });
  const err = extractErrMsg(m.error);
  return (
    <Modal open={open} title="New term" onCancel={() => { m.reset(); onClose(); }}
      okText="Add" confirmLoading={m.isPending} onOk={() => form.submit()} destroyOnClose>
      <Form form={form} layout="vertical"
        onFinish={(v) => m.mutate({
          session_id: sessionId, label: v.label.trim(),
          starts_on: v.starts_on, ends_on: v.ends_on,
        })}>
        <Form.Item name="label" label="Label" rules={[{ required: true }]}><Input placeholder="e.g. Term 1" /></Form.Item>
        <Space size={12}>
          <Form.Item name="starts_on" label="Starts" rules={[{ required: true }]}>
            <Input placeholder="yyyy-mm-dd" style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }} />
          </Form.Item>
          <Form.Item name="ends_on" label="Ends" rules={[{ required: true }]}>
            <Input placeholder="yyyy-mm-dd" style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }} />
          </Form.Item>
        </Space>
        {err && <Alert type="error" showIcon message={err} />}
      </Form>
    </Modal>
  );
}

function HolidayModal({
  open, institution, sessionId, onClose, onDone,
}: { open: boolean; institution: string; sessionId: string; onClose: () => void; onDone: () => void }) {
  const [form] = Form.useForm<CreateHolidayRequest>();
  const m = useMutation({
    mutationFn: (input: CreateHolidayRequest) => productApi.createHoliday(institution, input),
    onSuccess: () => { form.resetFields(); onDone(); onClose(); void message.success('Holiday added'); },
  });
  const err = extractErrMsg(m.error);
  return (
    <Modal open={open} title="New holiday" onCancel={() => { m.reset(); onClose(); }}
      okText="Add" confirmLoading={m.isPending} onOk={() => form.submit()} destroyOnClose>
      <Form form={form} layout="vertical"
        onFinish={(v) => m.mutate({ session_id: sessionId, date: v.date, label: v.label.trim() })}>
        <Form.Item name="date" label="Date" rules={[{ required: true }]}>
          <Input placeholder="yyyy-mm-dd" style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }} />
        </Form.Item>
        <Form.Item name="label" label="Label" rules={[{ required: true }]}><Input placeholder="e.g. Independence Day" /></Form.Item>
        {err && <Alert type="error" showIcon message={err} />}
      </Form>
    </Modal>
  );
}

/* =================== Subjects =================== */

export function SubjectsPage() {
  const { institution = '' } = useParams<{ institution: string }>();
  const qc = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const [renaming, setRenaming] = useState<SubjectRow | null>(null);
  const q = useQuery({
    queryKey: ['subjects', institution],
    queryFn: () => productApi.listSubjects(institution),
    enabled: !!institution,
  });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['subjects', institution] });
  const del = useMutation({
    mutationFn: (id: string) => productApi.deleteSubject(institution, id),
    onSuccess: () => { invalidate(); void message.success('Deleted'); },
    onError: (e) => { void message.error(extractErrMsg(e) ?? 'Delete failed'); },
  });

  if (q.isLoading || !q.data) return <Skeleton active paragraph={{ rows: 6 }} />;

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
        <div style={{ flex: 1 }}>
          <Typography.Title level={3} style={{ margin: 0 }}>Subjects</Typography.Title>
          <Typography.Text type="secondary" style={{ fontSize: 12.5 }}>{q.data.total} subjects</Typography.Text>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddOpen(true)}>New subject</Button>
      </div>

      <Card size="small">
        {q.data.rows.length === 0
          ? <Empty description="No subjects yet." style={{ padding: 20 }} />
          : (
            <Table<SubjectRow>
              size="small" pagination={{ pageSize: 20 }} rowKey="id"
              dataSource={q.data.rows}
              columns={[
                { title: 'Code', dataIndex: 'code', key: 'c', width: 140,
                  render: (v: string) => <Tag style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>{v}</Tag> },
                { title: 'Name', dataIndex: 'name', key: 'n' },
                { title: '', key: 'act', width: 140,
                  render: (_, r) => <Space size={4}>
                    <Button size="small" icon={<EditOutlined />} onClick={() => setRenaming(r)}>Rename</Button>
                    <Popconfirm title="Delete subject?" onConfirm={() => del.mutate(r.id)}>
                      <Button size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                  </Space> },
              ]}
            />
          )}
      </Card>

      <SubjectAddModal open={addOpen} institution={institution} onClose={() => setAddOpen(false)} onDone={invalidate} />
      <SubjectRenameModal target={renaming} institution={institution}
        onClose={() => setRenaming(null)} onDone={invalidate} />
    </Space>
  );
}

function SubjectAddModal({
  open, institution, onClose, onDone,
}: { open: boolean; institution: string; onClose: () => void; onDone: () => void }) {
  const [form] = Form.useForm<CreateSubjectRequest>();
  const m = useMutation({
    mutationFn: (input: CreateSubjectRequest) => productApi.createSubject(institution, input),
    onSuccess: () => { form.resetFields(); onDone(); onClose(); void message.success('Added'); },
  });
  const err = extractErrMsg(m.error);
  return (
    <Modal open={open} title="New subject" onCancel={() => { m.reset(); onClose(); }}
      okText="Add" confirmLoading={m.isPending} onOk={() => form.submit()} destroyOnClose>
      <Form form={form} layout="vertical"
        onFinish={(v) => m.mutate({ code: v.code.trim(), name: v.name.trim() })}>
        <Form.Item name="code" label="Code" rules={[{ required: true, max: 30 }]}>
          <Input placeholder="e.g. MATH" style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }} />
        </Form.Item>
        <Form.Item name="name" label="Name" rules={[{ required: true, max: 160 }]}><Input placeholder="Mathematics" /></Form.Item>
        {err && <Alert type="error" showIcon message={err} />}
      </Form>
    </Modal>
  );
}

function SubjectRenameModal({
  target, institution, onClose, onDone,
}: { target: SubjectRow | null; institution: string; onClose: () => void; onDone: () => void }) {
  const [name, setName] = useState('');
  const m = useMutation({
    mutationFn: () => productApi.updateSubject(institution, target!.id, { name: name.trim() }),
    onSuccess: () => { onDone(); onClose(); void message.success('Renamed'); },
  });
  return (
    <Modal open={!!target} title={target ? `Rename ${target.code}` : ''}
      onCancel={() => { m.reset(); onClose(); }}
      okText="Save" confirmLoading={m.isPending} onOk={() => m.mutate()} destroyOnClose>
      {target && (
        <Input value={name || target.name} onChange={(e) => setName(e.target.value)} />
      )}
    </Modal>
  );
}

/* =================== Programs =================== */

export function ProgramsPage() {
  const { institution = '' } = useParams<{ institution: string }>();
  const qc = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const q = useQuery({
    queryKey: ['programs', institution],
    queryFn: () => productApi.listPrograms(institution),
    enabled: !!institution,
  });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['programs', institution] });
  const toggle = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      productApi.updateProgram(institution, id, { active }),
    onSuccess: () => invalidate(),
  });

  if (q.isLoading || !q.data) return <Skeleton active paragraph={{ rows: 6 }} />;

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
        <div style={{ flex: 1 }}>
          <Typography.Title level={3} style={{ margin: 0 }}>Programs</Typography.Title>
          <Typography.Text type="secondary" style={{ fontSize: 12.5 }}>
            T-10 college axis: BA / BSc / BCom … Grades still model year (Y1..Y4) inside a program.
          </Typography.Text>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddOpen(true)}>New program</Button>
      </div>

      <Card size="small">
        {q.data.rows.length === 0
          ? <Empty description="No programs yet. Add one to unlock college-mode reports." style={{ padding: 20 }} />
          : (
            <Table<ProgramRow>
              size="small" pagination={{ pageSize: 20 }} rowKey="id"
              dataSource={q.data.rows}
              columns={[
                { title: 'Code', dataIndex: 'code', key: 'c', width: 130,
                  render: (v: string) => <Tag style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>{v}</Tag> },
                { title: 'Name', dataIndex: 'name', key: 'n' },
                { title: 'Duration', dataIndex: 'duration_years', key: 'd', width: 110,
                  render: (v: number) => `${v} year${v === 1 ? '' : 's'}` },
                { title: 'Active', dataIndex: 'active', key: 'a', width: 90,
                  render: (v: boolean, r) => (
                    <Switch size="small" checked={v}
                      onChange={(next) => toggle.mutate({ id: r.id, active: next })} />
                  ) },
                { title: 'Note', dataIndex: 'note', key: 'note',
                  render: (v: string | null) => v ? <span style={{ fontSize: 12 }}>{v}</span> : <Typography.Text type="secondary">—</Typography.Text> },
              ]}
            />
          )}
      </Card>

      <ProgramAddModal open={addOpen} institution={institution} onClose={() => setAddOpen(false)} onDone={invalidate} />
    </Space>
  );
}

function ProgramAddModal({
  open, institution, onClose, onDone,
}: { open: boolean; institution: string; onClose: () => void; onDone: () => void }) {
  const [form] = Form.useForm<CreateProgramRequest>();
  const m = useMutation({
    mutationFn: (input: CreateProgramRequest) => productApi.createProgram(institution, input),
    onSuccess: () => { form.resetFields(); onDone(); onClose(); void message.success('Added'); },
  });
  const err = extractErrMsg(m.error);
  return (
    <Modal open={open} title="New program" onCancel={() => { m.reset(); onClose(); }}
      okText="Add" confirmLoading={m.isPending} onOk={() => form.submit()} destroyOnClose>
      <Form form={form} layout="vertical" initialValues={{ duration_years: 3 }}
        onFinish={(v) => m.mutate({
          code: v.code.trim().toUpperCase(),
          name: v.name.trim(),
          duration_years: v.duration_years,
          note: v.note?.trim() || undefined,
        })}>
        <Form.Item name="code" label="Code" rules={[{ required: true, max: 30 }]}>
          <Input placeholder="e.g. BSC" style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }} />
        </Form.Item>
        <Form.Item name="name" label="Name" rules={[{ required: true, max: 160 }]}><Input placeholder="Bachelor of Science" /></Form.Item>
        <Form.Item name="duration_years" label="Duration (years)" rules={[{ required: true, type: 'number', min: 1, max: 10 }]}>
          <InputNumber min={1} max={10} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="note" label="Note (optional)"><Input.TextArea rows={2} /></Form.Item>
        {err && <Alert type="error" showIcon message={err} />}
      </Form>
    </Modal>
  );
}

/* =================== Rooms =================== */

const ROOM_KINDS: RoomKind[] = ['classroom', 'lab', 'hall', 'office', 'other'];
const ROOM_TONE: Record<RoomKind, string> = {
  classroom: 'blue', lab: 'geekblue', hall: 'purple', office: 'default', other: 'default',
};

export function RoomsPage() {
  const { institution = '' } = useParams<{ institution: string }>();
  const qc = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const q = useQuery({
    queryKey: ['rooms', institution],
    queryFn: () => productApi.listRooms(institution),
    enabled: !!institution,
  });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['rooms', institution] });
  const toggle = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      productApi.updateRoom(institution, id, { active }),
    onSuccess: () => invalidate(),
  });

  if (q.isLoading || !q.data) return <Skeleton active paragraph={{ rows: 6 }} />;

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
        <div style={{ flex: 1 }}>
          <Typography.Title level={3} style={{ margin: 0 }}>Rooms</Typography.Title>
          <Typography.Text type="secondary" style={{ fontSize: 12.5 }}>
            {q.data.total} rooms · {q.data.total_capacity} seats total
          </Typography.Text>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddOpen(true)}>New room</Button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 8 }}>
        {ROOM_KINDS.map((k) => (
          <Card key={k} size="small">
            <div style={{ fontSize: 11, color: '#8a94a6', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>{k}</div>
            <div style={{ fontSize: 15, fontWeight: 700 }}>{q.data.by_kind[k] ?? 0}</div>
          </Card>
        ))}
      </div>

      <Card size="small">
        {q.data.rows.length === 0
          ? <Empty description="No rooms yet." style={{ padding: 20 }} />
          : (
            <Table<RoomRow>
              size="small" pagination={{ pageSize: 20 }} rowKey="id"
              dataSource={q.data.rows}
              columns={[
                { title: 'Code', dataIndex: 'code', key: 'c', width: 130,
                  render: (v: string) => <Tag style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>{v}</Tag> },
                { title: 'Name', dataIndex: 'name', key: 'n' },
                { title: 'Kind', dataIndex: 'kind', key: 'k', width: 110,
                  render: (v: RoomKind) => <Tag color={ROOM_TONE[v]}>{v}</Tag> },
                { title: 'Floor', dataIndex: 'floor', key: 'f', width: 90,
                  render: (v: string | null) => v ?? <Typography.Text type="secondary">—</Typography.Text> },
                { title: 'Capacity', dataIndex: 'capacity', key: 'cap', width: 100, align: 'right' },
                { title: 'Active', dataIndex: 'active', key: 'a', width: 90,
                  render: (v: boolean, r) => (
                    <Switch size="small" checked={v}
                      onChange={(next) => toggle.mutate({ id: r.id, active: next })} />
                  ) },
              ]}
            />
          )}
      </Card>

      <RoomAddModal open={addOpen} institution={institution} onClose={() => setAddOpen(false)} onDone={invalidate} />
    </Space>
  );
}

function RoomAddModal({
  open, institution, onClose, onDone,
}: { open: boolean; institution: string; onClose: () => void; onDone: () => void }) {
  const [form] = Form.useForm<CreateRoomRequest>();
  const m = useMutation({
    mutationFn: (input: CreateRoomRequest) => productApi.createRoom(institution, input),
    onSuccess: () => { form.resetFields(); onDone(); onClose(); void message.success('Added'); },
  });
  const err = extractErrMsg(m.error);
  return (
    <Modal open={open} title="New room" onCancel={() => { m.reset(); onClose(); }}
      okText="Add" confirmLoading={m.isPending} onOk={() => form.submit()} destroyOnClose>
      <Form form={form} layout="vertical" initialValues={{ kind: 'classroom' as RoomKind, capacity: 40 }}
        onFinish={(v) => m.mutate({
          code: v.code.trim().toUpperCase(),
          name: v.name.trim(),
          kind: v.kind,
          capacity: v.capacity,
          floor: v.floor?.trim() || undefined,
          note: v.note?.trim() || undefined,
        })}>
        <Form.Item name="code" label="Code" rules={[{ required: true, max: 30 }]}>
          <Input placeholder="e.g. R-101" style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }} />
        </Form.Item>
        <Form.Item name="name" label="Name" rules={[{ required: true, max: 160 }]}><Input placeholder="Room 101 / Physics Lab" /></Form.Item>
        <Space size={12}>
          <Form.Item name="kind" label="Kind" rules={[{ required: true }]}>
            <Select style={{ width: 140 }} options={ROOM_KINDS.map((k) => ({ value: k, label: k }))} />
          </Form.Item>
          <Form.Item name="capacity" label="Capacity" rules={[{ required: true, type: 'number', min: 0 }]}>
            <InputNumber min={0} max={2000} />
          </Form.Item>
          <Form.Item name="floor" label="Floor"><Input placeholder="e.g. 1F" style={{ width: 100 }} /></Form.Item>
        </Space>
        <Form.Item name="note" label="Note (optional)"><Input.TextArea rows={2} /></Form.Item>
        {err && <Alert type="error" showIcon message={err} />}
      </Form>
    </Modal>
  );
}

